import api from '../lib/api';
import coloradoDistrictPdfUrls from '../data/coloradoDistrictPdfUrls.json';

type ColoradoDistrictPdfData = {
  house: Record<string, string>;
  senate: Record<string, string>;
  source?: string;
  fetchedAt?: string;
};

const co = coloradoDistrictPdfUrls as ColoradoDistrictPdfData;

/**
 * Only http(s) URLs may be used in district map anchors (defense in depth if stored data is ever malformed).
 * Absolute URLs must start with http:// or https://. Root-relative paths (e.g. /api/files/serve/…) are allowed when `window` exists.
 */
export function sanitizeDistrictMapHref(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    let parsed: URL;
    if (/^https?:\/\//i.test(trimmed)) {
      parsed = new URL(trimmed);
    } else if (trimmed.startsWith('/')) {
      const origin =
        typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
      if (!origin) return null;
      parsed = new URL(trimmed, origin);
    } else {
      return null;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.href;
  } catch {
    return null;
  }
}

/** In-flight and resolved signed URLs from GET /api/district-maps (key: House|7) */
const hostedUrlCache = new Map<string, string | null>();
const hostedInflight = new Map<string, Promise<string | null>>();

export function parseDistrictNumber(
  district: string | number | null | undefined
): number | null {
  if (district === null || district === undefined) return null;
  const s = String(district).trim();
  if (!s) return null;
  const digits = s.replace(/\D/g, '');
  const n = parseInt(digits || s, 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Stable Colorado Independent Redistricting Commissions PDF for a legislative district.
 * URLs are captured in src/data/coloradoDistrictPdfUrls.json (regenerate via scripts/fetchColoradoDistrictPdfUrls.mjs).
 */
export function getColoradoCommissionDistrictPdfUrl(
  chamber: string | undefined,
  district: string | number | null | undefined
): string | null {
  if (!chamber) return null;
  const n = parseDistrictNumber(district);
  if (n === null) return null;
  const c = chamber.toLowerCase();
  if (c.includes('house')) {
    if (n < 1 || n > 65) return null;
    return sanitizeDistrictMapHref(co.house[String(n)] ?? null);
  }
  if (c.includes('senate')) {
    if (n < 1 || n > 35) return null;
    return sanitizeDistrictMapHref(co.senate[String(n)] ?? null);
  }
  return null;
}

function normalizeChamberForApi(chamber: string): 'House' | 'Senate' | null {
  const c = chamber.toLowerCase();
  if (c.includes('senate')) return 'Senate';
  if (c.includes('house')) return 'House';
  return null;
}

/**
 * Canonical district string used for both the cache key and API params.
 * Strips leading zeros / non-digits (e.g. "08" -> "8") so callers that pass
 * zero-padded or differently formatted districts share one cache entry and the
 * key matches the backend's normalized lookup.
 */
function normalizeDistrictForApi(district: string | number): string {
  const n = parseDistrictNumber(district);
  return n !== null ? String(n) : String(district).trim();
}

/**
 * Returns a signed URL for the hosted district map PDF, or null if none / error.
 * Deduplicates concurrent requests per (chamber, district), including against an
 * in-flight batch prefetch (see prefetchHostedDistrictMapUrls).
 */
export function fetchHostedDistrictMapUrlOnce(
  chamber: string,
  district: string | number
): Promise<string | null> {
  const ch = normalizeChamberForApi(chamber);
  if (!ch) return Promise.resolve(null);
  const apiDistrict = normalizeDistrictForApi(district);
  const key = `${ch}|${apiDistrict}`;
  if (hostedUrlCache.has(key)) {
    return Promise.resolve(hostedUrlCache.get(key)!);
  }
  const inflight = hostedInflight.get(key);
  if (inflight) return inflight;

  const p = api
    .get('/district-maps', {
      params: { state: 'CO', chamber: ch, district: apiDistrict },
    })
    .then((res) => {
      const url = res.data?.data?.map?.url ?? null;
      const safe = sanitizeDistrictMapHref(url);
      hostedUrlCache.set(key, safe);
      return safe;
    })
    .catch(() => {
      hostedUrlCache.set(key, null);
      return null;
    })
    .finally(() => {
      hostedInflight.delete(key);
    });

  hostedInflight.set(key, p);
  return p;
}

/**
 * Resolve many hosted district map URLs in a single request and seed the cache,
 * so individual DistrictMapLink mounts read from cache instead of each firing
 * their own GET /district-maps (the N+1 pattern). Already-cached / in-flight
 * districts are skipped. Safe to call repeatedly per list render.
 */
export function prefetchHostedDistrictMapUrls(
  items: Array<{ chamber: string | undefined | null; district: string | number | null | undefined }>
): Promise<void> {
  const toFetch = new Map<string, { chamber: 'House' | 'Senate'; district: string }>();
  for (const item of items) {
    if (!item.chamber) continue;
    const ch = normalizeChamberForApi(item.chamber);
    if (!ch) continue;
    const n = parseDistrictNumber(item.district);
    if (n === null) continue;
    const district = String(n);
    const key = `${ch}|${district}`;
    if (hostedUrlCache.has(key) || hostedInflight.has(key) || toFetch.has(key)) continue;
    toFetch.set(key, { chamber: ch, district });
  }
  if (toFetch.size === 0) return Promise.resolve();

  const keys = Array.from(toFetch.keys());
  const batchPromise = api
    .post('/district-maps/batch', { state: 'CO', items: Array.from(toFetch.values()) })
    .then((res) => {
      const maps = res.data?.data?.maps ?? {};
      for (const key of keys) {
        const safe = sanitizeDistrictMapHref(maps[key]?.url ?? null);
        hostedUrlCache.set(key, safe);
      }
    })
    .catch(() => {
      // Leave cache unset on failure so individual links can retry via single GET.
    })
    .finally(() => {
      for (const key of keys) {
        hostedInflight.delete(key);
      }
    });

  // Register per-key in-flight entries so concurrent single fetches dedupe against the batch.
  for (const key of keys) {
    hostedInflight.set(key, batchPromise.then(() => hostedUrlCache.get(key) ?? null));
  }

  return batchPromise;
}
